import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { DocumentEndeavorApi } from '../../services/api/DocumentEndeavorAPI';
import { DocumentApi } from '../../services/api/DocumentApi';
import { FileApi } from '../../services/api/FileApi';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import RichMarkdown from '../common/RichMarkdown';
import { useAiChat, invalidateConversationCache } from '../../hooks/useAiChat';
import useSpeechToText from '../../hooks/useSpeechToText';
import useComposerDropzone from '../../hooks/useComposerDropzone';
import DocumentSelectionModal from '../documents/DocumentSelectionModal';
import AttachDocumentModal from './AttachDocumentModal';
import './AiChatCard.css';
import '../primitives/Button.css';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import usePremiumAccess from '../../hooks/usePremiumAccess';
import UpgradeModal from '../common/UpgradeModal';

const botHead = '/scout_images/Scout_Thumbsup.png';

// ── Course chat conversation ID cache (survives component remounts) ──
const COURSE_CONV_CACHE_KEY = 'courseChat_convMap';
function getCourseConvId(courseId) {
  try {
    const map = JSON.parse(sessionStorage.getItem(COURSE_CONV_CACHE_KEY) || '{}');
    return map[String(courseId)] || null;
  } catch { return null; }
}
function setCourseConvId(courseId, conversationId) {
  try {
    const map = JSON.parse(sessionStorage.getItem(COURSE_CONV_CACHE_KEY) || '{}');
    map[String(courseId)] = conversationId;
    sessionStorage.setItem(COURSE_CONV_CACHE_KEY, JSON.stringify(map));
  } catch {}
}

// ─── Thinking status labels ───
const THINKING_LABELS_WITH_DOCS = [
  'Reviewing course documents',
  'Analyzing selected materials',
  'Grounding response in documents',
];
const THINKING_LABELS_NO_DOCS = [
  'Thinking',
  'Processing',
  'Building answer',
];

/** Returns a rotating status label that advances every ~2s. */
function useThinkingLabel(isActive, hasDocContext) {
  const labels = hasDocContext ? THINKING_LABELS_WITH_DOCS : THINKING_LABELS_NO_DOCS;
  const [idx, setIdx] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) { setIdx(0); startRef.current = null; return; }
    startRef.current = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - (startRef.current || Date.now());
      // Rotate: 0→1 at 2s, 1→2 at 4s, stay at last
      const next = Math.min(Math.floor(elapsed / 2000), labels.length - 1);
      setIdx(next);
    }, 600);
    return () => clearInterval(timer);
  }, [isActive, labels.length]);

  return labels[idx] || labels[0];
}

// Scout welcome greetings - randomly selected for new chats
const SCOUT_GREETINGS = [
  "Hey friend. Ready when you are!",
  "Scout here. Let's get started!",
  "What are we working on today?",
  "I am ready. Just tell me what you need.",
  "Let's learn something. What's up?",
  "Show me the next thing.",
  "I am here. Ask me anything!",
  "Let's dive in! What's first?",
  "Ready to help. Fire away!",
  "I am locked in. What do you got?"
];

/**
 * Props:
 * mode: 'standard' | 'endeavor' | 'document'
 * endeavorId / documentId: context IDs
 * conversation (object from history) or null
 * onUpdateConversationTitle(conversationId,title)
 * onConversationActivated(conversationObj) - called when conversation is hydrated or created by backend
 * onUserMessageSent(conversationId, userMessage) OPTIONAL: invoked after a user message is queued & conversation ensured
 * 
 * Note: Conversation creation is now handled by the backend when the first message is sent.
 * The frontend no longer creates empty conversations upfront.
 */
export default function AiChatCard(props) {
  const {
    variant = 'default',
    mode = 'standard',
    endeavorId,
    endeavorName,
    conversation,
    onUpdateConversationTitle,
    onConversationActivated,
    documentsRefreshKey,
    documentId,
    documentName,
    onUserMessageSent,
    isExpanded = false,
    onToggleExpansion,
    initialQuality,
    externalSelectedDocuments,
    onExternalSelectedDocsChange,
    externalShowDocModal,
    onExternalDocModalChange,
    onHydratedDocsLoaded,
    hideHeader = false,
  } = props;

  const { user } = useAuth();
  const dataContext = useData();

  // TEMP: premium gating — block new message send when user cannot create chat content
  const { canCreate, tier: currentTier } = usePremiumAccess();
  const [showChatUpgradeModal, setShowChatUpgradeModal] = useState(false);
  
  // Ensure document types are loaded for quick-upload (uses cache if available)
  useEffect(() => {
    if ((!dataContext?.documentTypes || dataContext.documentTypes.length === 0) && dataContext?.fetchDocumentTypes) {
      dataContext.fetchDocumentTypes();
    }
  }, [dataContext]);

  // All documents are now selectable — AI service handles both RAG-processed and unprocessed docs
  const requiredStatusIds = [];
  
  const {
    isAiServiceAvailable,
    loadExistingConversations,
    loadConversationMessages,
    loadConversationDetails,
    loadMoreMessages,
    updateConversationTitle: _updateConversationTitle, // eslint-disable-line no-unused-vars
    patchConversation,
    // remove non-stream send
    getQualityLevelOptions,
    processUserMessageStream
  } = useAiChat(endeavorId || 'all');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isInputExpanded, setIsInputExpanded] = useState(false);
  // Default quality changed from 'balanced' to 'fast' per request
  // Use initialQuality prop if provided (for ScoutIQ dashboard control)
  const [quality, setQuality] = useState(initialQuality || 'fast');
  
  // Sync quality when initialQuality prop changes (ScoutIQ dashboard control)
  useEffect(() => {
    if (initialQuality && initialQuality !== quality) {
      setQuality(initialQuality);
    }
  }, [initialQuality, quality]);
  // const conversationId = props.conversationId;

  // streaming handle is not used yet; remove when unused to avoid warnings
  const activeStreamRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);                      // Quick-upload file input
  const creatingEndeavorConvRef = useRef(false);          // ✅ NEW: prevents duplicate creation
  const creatingDocumentConvRef = useRef(false);          // ✅ NEW: prevents duplicate creation for document mode
  const renameSavingRef = useRef(false);

  // ── Buffered streaming refs ──
  const streamBufferRef = useRef('');       // raw tokens not yet flushed to display
  const flushTimerRef = useRef(null);       // setInterval id for flush cadence
  const streamAiIdRef = useRef(null);       // which message id is currently streaming
  const lastScrollFlushRef = useRef(0);     // timestamp of last scroll during flush
  const [showQuality, setShowQuality] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);  // Quick-upload in progress
  const [docNames, setDocNames] = useState({});            // Resolved doc names { id: name }
  const [isHydrating, setIsHydrating] = useState(false); // NEW: indicates initial conversation/message load in mode-specific hydrate
  const [showDocModal, setShowDocModal] = useState(false);                     // ✅ added - MUST be before usage
  const [showAttachModal, setShowAttachModal] = useState(false);               // Choice modal for "+" button
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameInput, setRenameInput] = useState('');
  const [internalSelectedDocuments, setInternalSelectedDocuments] = useState([]); // internal fallback
  const [docsCleared, setDocsCleared] = useState(false); // NEW: user intentionally removed all docs
  
  // Use external state when provided (Scout's Desk), otherwise use internal state
  const selectedDocuments = externalSelectedDocuments !== undefined ? externalSelectedDocuments : internalSelectedDocuments;
  const setSelectedDocuments = onExternalSelectedDocsChange || setInternalSelectedDocuments;
  const isDocModalOpen = externalShowDocModal !== undefined ? externalShowDocModal : showDocModal;
  const setIsDocModalOpen = onExternalDocModalChange || setShowDocModal;
  const [autoDocsLoaded, setAutoDocsLoaded] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Speech-to-text
  const { isListening, isSupported: isSpeechSupported, startListening, stopListening } = useSpeechToText({
    getInputValue: () => input,
    setInputValue: setInput,
  });

  // Pending file uploads (drag-and-drop / paste)
  const [pendingUploads, setPendingUploads] = useState([]);
  // Accepted file extensions for validation
  const ACCEPTED_EXTENSIONS = new Set(['.pdf','.docx','.pptx','.txt','.md','.csv','.json','.html','.rtf','.jpg','.jpeg','.png','.tiff','.tif','.bmp','.gif','.webp']);
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

  const handleDroppedFiles = useCallback(async (files) => {
    if (mode === 'endeavor') return;
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ACCEPTED_EXTENSIONS.has(ext)) {
        setPendingUploads(prev => [...prev, { localId: `pu_${Date.now()}_${Math.random()}`, name: file.name, status: 'error', error: 'Unsupported file type' }]);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setPendingUploads(prev => [...prev, { localId: `pu_${Date.now()}_${Math.random()}`, name: file.name, status: 'error', error: 'File is too large (max 50 MB)' }]);
        continue;
      }
      const localId = `pu_${Date.now()}_${Math.random()}`;
      const isImage = file.type.startsWith('image/');
      let thumbUrl = null;
      if (isImage) {
        try { thumbUrl = URL.createObjectURL(file); } catch {}
      }
      setPendingUploads(prev => [...prev, { localId, name: file.name, status: 'uploading', isImage, thumbUrl }]);
      try {
        const docTypes = dataContext?.documentTypes || [];
        const articleType = docTypes.find(dt => dt.name === 'Article');
        const metadata = {
          name: file.name,
          documentTypeId: articleType?.id || undefined,
          userId: user?.user_id || user?.userId || user?.id,
          description: `Uploaded file: ${file.name}`,
        };
        const response = await FileApi.uploadFile(file, metadata);
        if (!response.ok) {
          setPendingUploads(prev => prev.map(p => p.localId === localId ? { ...p, status: 'error', error: 'Upload failed' } : p));
          continue;
        }
        const data = await response.json();
        const documentId = data.documentId || data.document?.documentId || data.document?.document_id || data.id;
        if (documentId) {
          const docIdStr = String(documentId);
          setSelectedDocuments(prev => prev.includes(docIdStr) ? prev : [...prev, docIdStr]);
          if (docsCleared) setDocsCleared(false);
          setDocNames(prev => ({ ...prev, [docIdStr]: file.name }));
          setPendingUploads(prev => prev.filter(p => p.localId !== localId));
          try {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('documents:changed', { detail: { source: 'DragDropUpload', fileName: file.name, documentId } }));
            }, 140);
          } catch {}
        } else {
          setPendingUploads(prev => prev.map(p => p.localId === localId ? { ...p, status: 'error', error: 'Upload failed' } : p));
        }
      } catch {
        setPendingUploads(prev => prev.map(p => p.localId === localId ? { ...p, status: 'error', error: 'Upload failed' } : p));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, user, dataContext, setSelectedDocuments, docsCleared]);

  const removePendingUpload = useCallback((localId) => {
    setPendingUploads(prev => {
      const item = prev.find(p => p.localId === localId);
      if (item?.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
      return prev.filter(p => p.localId !== localId);
    });
  }, []);

  const { isDragging, dropzoneHandlers } = useComposerDropzone({
    onFiles: handleDroppedFiles,
    disabled: mode === 'endeavor' || isLoading || isHydrating || !isAiServiceAvailable,
  });

  const hasAnyUploading = pendingUploads.some(p => p.status === 'uploading');
  const [internalConversation, setInternalConversation] = useState(null);      // ✅ added
  const [messagePagination, setMessagePagination] = useState(null);            // ✅ added
  const [loadingMore, setLoadingMore] = useState(false);                       // ✅ added
  const [attachedEndeavorIds, setAttachedEndeavorIds] = useState([]);          // ✅ NEW: hydrate persisted endeavor_ids
  const [expandedRefs, setExpandedRefs] = useState(new Set()); // ✅ track which messages have expanded doc refs
  const [copiedMsgId, setCopiedMsgId] = useState(null); // track last copied message id
  const lastConvIdRef = useRef(null); // track last conversation id to detect switches (simplified)
  const [scoutGreeting, setScoutGreeting] = useState(null); // Scout's welcome message for new chats
  const hasUserMessages = messages.filter(m => m.sender === 'user').length > 0;

  // Set initial greeting on component mount if appropriate
  useEffect(() => {
    if (!scoutGreeting && !hasUserMessages && !isHydrating) {
      const randomGreeting = SCOUT_GREETINGS[Math.floor(Math.random() * SCOUT_GREETINGS.length)];
      setScoutGreeting(randomGreeting);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // ===== Course/Document Chat Detection =====
  const desiredCourseTitle = endeavorName ? `Course: ${endeavorName}` : null;
  const desiredDocumentTitle = documentName ? `Document: ${documentName}` : null;

  const titleLooksCourse = (conversation?.title || '').toLowerCase().startsWith('course: ');

  // Locking rules: ONLY by page mode or explicit title prefix (do not re-declare later)
  // Support dynamic mode switching based on props
  const isCourseChat   = (mode === 'endeavor') || titleLooksCourse;
  // isDocumentChat: true only when in single-document mode, NOT just because the title has a prefix.
  // A chat that started as single-doc but had more docs added is now multi-doc and should be renameable.
  const isDocumentChat = (mode === 'document');
  
  // When mode prop changes, update internal state accordingly
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      prevModeRef.current = mode;
      // Mode changed - component will naturally re-render with new mode-dependent values
    }
  }, [mode]);

  // NEW: In standard mode, require an active conversation (from ChatHistoryPanel)
  // Exception: Scout's Desk (scoutIqDashboard) and AI Tools (aitools) manage conversations differently - backend creates on first message
  const noActiveConversation = mode === 'standard' && variant !== 'scoutIqDashboard' && variant !== 'aitools' && !(conversation || internalConversation) && !props.autoCreateIfNone;

  // Compute current title (fallbacks)
  const currentTitle =
    conversation?.title ||
    internalConversation?.title ||
    (isCourseChat && desiredCourseTitle) ||
    (mode === 'document' && desiredDocumentTitle) ||
    'New Chat';

  // Resolved display title for header: resolve course name from DataContext if stored title is generic
  const displayTitle = useMemo(() => {
    const raw = currentTitle.replace(/^Course:\s*/i, '').replace(/^Document:\s*/i, '');
    if (!isCourseChat) return raw;
    // If the stripped title is the real course name, use it
    if (endeavorName && raw === endeavorName) return raw;
    // Try resolving from coursesById for conversations with attachedEndeavorIds
    const conv = conversation || internalConversation;
    const eids = conv?.attachedEndeavorIds || (endeavorId ? [endeavorId] : []);
    const coursesById = dataContext?.state?.coursesById || {};
    for (const eid of eids) {
      const course = coursesById[eid] || coursesById[String(eid)];
      if (course) return course.name || course.title || course.endeavorName || raw;
    }
    // Fall back to endeavorName prop if available and the stored title looks generic
    if (endeavorName && endeavorName !== 'Course' && (raw === 'Course' || raw === 'Unknown Course' || !raw)) {
      return endeavorName;
    }
    return raw || 'New Chat';
  }, [currentTitle, isCourseChat, endeavorName, endeavorId, conversation, internalConversation, dataContext?.state?.coursesById]);
  
  useEffect(() => {
  }, [currentTitle, conversation?.title, internalConversation?.title, conversation?.conversationId, internalConversation?.conversationId]);

  // ===== Auto-scroll + scroll-to-bottom arrow =====
  const userAutoScrollRef = useRef(true); // true while user hasn't scrolled far up
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const scrollChatToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (!userAutoScrollRef.current && distanceFromBottom >= 40) return;
    if (distanceFromBottom < 120 || userAutoScrollRef.current) el.scrollTop = el.scrollHeight;
  }, []);

  const handleScrollToBottomClick = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    userAutoScrollRef.current = true;
    setShowScrollBtn(false);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  // Track manual user scroll to disable aggressive auto-scroll & show arrow
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 150) {
      userAutoScrollRef.current = false;
      setShowScrollBtn(true);
    } else if (distanceFromBottom < 80) {
      userAutoScrollRef.current = true;
      setShowScrollBtn(false);
    }
  }, []);

  // Force an initial scroll-to-bottom whenever the active conversation changes or hydrates.
  useEffect(() => {
    const convId = (conversation?.conversationId || conversation?.id || internalConversation?.conversationId || internalConversation?.id);
    if (!convId) return; // nothing active yet
    const prev = userAutoScrollRef.current;
    userAutoScrollRef.current = true; // allow force scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollChatToBottom();
        userAutoScrollRef.current = prev; // restore prior intent
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.conversationId, internalConversation?.conversationId, scrollChatToBottom]);

  // Always scroll after message list changes (new message or system update)
  useEffect(() => {
    if (messages.length > 0) {
      scrollChatToBottom();
    }
  }, [messages, scrollChatToBottom]);

  // Reconcile messages on conversation change.
  // Simplified: Only reconcile when conversation prop changes externally
  // Do NOT reconcile during active streaming or when internalConversation changes
  useEffect(() => {
    // Skip reconciliation entirely during active streaming to prevent interference
    if (isLoading || activeStreamRef.current) {
      return;
    }
    
    // Handle new chat button: conversation set to null explicitly
    // Skip for endeavor/document mode — those use internalConversation and should never auto-clear
    if (!conversation && lastConvIdRef.current && mode !== 'endeavor' && mode !== 'document') {
      setMessages([]);
      setMessagePagination(null);
      setSelectedDocuments([]);
      setAttachedEndeavorIds([]);
      setInternalConversation(null);
      lastConvIdRef.current = null;
      try { activeStreamRef.current?.cancel?.(); } catch {}
      activeStreamRef.current = null;
      setIsLoading(false);
      // Reset greeting for new chat
      const randomGreeting = SCOUT_GREETINGS[Math.floor(Math.random() * SCOUT_GREETINGS.length)];
      setScoutGreeting(randomGreeting);
      return;
    }
    
    // Use conversation prop (not internalConversation) for reconciliation
    // internalConversation is only for tracking newly created conversations during streaming
    if (!conversation) {
      return;
    }
    
    const convId = conversation.conversationId || conversation.id;
    const incoming = Array.isArray(conversation.messages) ? conversation.messages : [];
    const incomingPagination = conversation.messagePagination || null;
    
    // Detect conversation switch
    const isSwitch = lastConvIdRef.current && lastConvIdRef.current !== convId;
    if (isSwitch) {
      try { activeStreamRef.current?.cancel?.(); } catch {}
      activeStreamRef.current = null;
      setIsLoading(false);
      // Always replace messages on a true switch. For a newly created blank conversation we want a clean slate.
      // Strip out any transient streaming states from loaded messages
      const cleanedIncoming = incoming.map(m => ({
        ...m,
        isStreaming: false,
        isThinking: false
      }));
      setMessages(cleanedIncoming);
      setMessagePagination(incomingPagination || null);
      lastConvIdRef.current = convId;
      // Update attached documents/endeavors ONLY if provided (don't clear existing selection with undefined/null)
      if (Array.isArray(conversation.attachedDocumentIds) && conversation.attachedDocumentIds.length > 0) {
        setSelectedDocuments(conversation.attachedDocumentIds.map(String));
      } else if (conversation.attachedDocumentIds === null || (Array.isArray(conversation.attachedDocumentIds) && conversation.attachedDocumentIds.length === 0)) {
        // Only clear if explicitly empty array or null (intentional clearing)
        setSelectedDocuments([]);
      }
      // else: leave selectedDocuments unchanged (backend didn't include field)
      
      if (Array.isArray(conversation.attachedEndeavorIds) && conversation.attachedEndeavorIds.length > 0) {
        setAttachedEndeavorIds(conversation.attachedEndeavorIds);
      } else if (conversation.attachedEndeavorIds === null || (Array.isArray(conversation.attachedEndeavorIds) && conversation.attachedEndeavorIds.length === 0)) {
        setAttachedEndeavorIds([]);
      }
      requestAnimationFrame(scrollChatToBottom);
      return;
    }

    // First time seeing this conversation - load it
    if (!lastConvIdRef.current) {
      console.log('[AiChatCard Reconciliation] Loading conversation for first time', { convId, incomingCount: incoming.length });
      lastConvIdRef.current = convId;
      const cleanedIncoming = incoming.map(m => ({
        ...m,
        isStreaming: false,
        isThinking: false
      }));
      setMessages(cleanedIncoming);
      setMessagePagination(incomingPagination || null);
      // Only update attachments if explicitly provided
      if (Array.isArray(conversation.attachedDocumentIds) && conversation.attachedDocumentIds.length > 0) {
        const docIds = conversation.attachedDocumentIds.map(String);
        setSelectedDocuments(docIds);
        // Notify parent of hydrated docs (for Scout's Desk state sync)
        onHydratedDocsLoaded?.({ conversationId: conversation.conversationId || conversation.id, docIds });
      }
      if (Array.isArray(conversation.attachedEndeavorIds) && conversation.attachedEndeavorIds.length > 0) {
        setAttachedEndeavorIds(conversation.attachedEndeavorIds);
      }
      requestAnimationFrame(scrollChatToBottom);
      return;
    }

    // Same conversation - only reconcile if incoming has MORE messages (parent loaded from backend)
    // Don't overwrite local state with stale/empty data from parent during active composition
    if (incoming.length > messages.length) {
      const cleanedIncoming = incoming.map(m => ({
        ...m,
        isStreaming: false,
        isThinking: false
      }));
      setMessages(cleanedIncoming);
      setMessagePagination(incomingPagination || null);
      requestAnimationFrame(scrollChatToBottom);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, isLoading, scrollChatToBottom, mode, messages.length]);

  // Hydrate existing endeavor conversation only (no auto creation until first prompt)
  useEffect(() => {
    const hydrate = async () => {
      if (mode !== 'endeavor') return;
      if (conversation || internalConversation) return;
      if (!endeavorName || !user) return;
      if (creatingEndeavorConvRef.current) return;
      creatingEndeavorConvRef.current = true;
      setIsHydrating(true);
      try {
        const desiredTitle = `Course: ${endeavorName}`;
        const eid = endeavorId ? String(endeavorId) : null;
        console.log('[AiChatCard hydrate] Starting endeavor hydration', { endeavorName, eid, desiredTitle });

        // 1) Try cached conversationId first (fastest, survives remounts)
        const cachedConvId = eid ? getCourseConvId(eid) : null;
        let match = null;

        if (cachedConvId) {
          console.log('[AiChatCard hydrate] Found cached convId', cachedConvId);
          try {
            const details = await loadConversationDetails(cachedConvId);
            if (details && (details.conversationId || details.id)) {
              match = details;
              console.log('[AiChatCard hydrate] Loaded from cache', { id: match.conversationId || match.id, title: match.title });
            }
          } catch (e) {
            console.warn('Endeavor hydrate: cached conv load failed', e);
          }
        }

        // 2) Fall back to searching the conversation list
        if (!match) {
          console.log('[AiChatCard hydrate] Cache miss, searching conversation list');
          invalidateConversationCache(user.id || user.userId);
          const existingList = await loadExistingConversations(user.id || user.userId);
          console.log('[AiChatCard hydrate] Got', existingList.length, 'conversations');
          // Exact title match first
          match = existingList.find(c => c.title === desiredTitle || c.title === endeavorName);
          // Then by attachedEndeavorIds
          if (!match && eid) {
            match = existingList.find(c =>
              Array.isArray(c.attachedEndeavorIds) &&
              c.attachedEndeavorIds.some(id => String(id) === eid)
            );
          }
          // Then by partial title match (case-insensitive) for old chats that may have Course: prefix with different casing
          if (!match) {
            const nameLower = endeavorName.toLowerCase();
            match = existingList.find(c => {
              const t = (c.title || '').toLowerCase();
              return t.includes(nameLower) || nameLower.includes(t.replace('course:', '').trim());
            });
          }
          if (match) {
            console.log('[AiChatCard hydrate] Found match in list', { id: match.id, title: match.title });
          } else {
            console.log('[AiChatCard hydrate] No match found');
          }
        }

        if (match) {
          const matchId = match.id || match.conversationId;
          // Repair title if it doesn't have the Course: prefix
          const matchTitle = (match.title || '').trim();
          const needsTitleRepair = !matchTitle.toLowerCase().startsWith('course:');
          const repairedTitle = needsTitleRepair ? desiredTitle : matchTitle;

          let loadedMsgs = [];
          let loadedPagination = null;
          try {
            const loaded = await loadConversationMessages(matchId, { page: 0, size: 50 });
            if (loaded && Array.isArray(loaded.messages)) loadedMsgs = loaded.messages;
            if (loaded && loaded.pagination) loadedPagination = loaded.pagination;
          } catch (e) {
            console.warn('Endeavor hydrate: loadConversationMessages failed', e);
          }
          const convObj = {
            ...match,
            conversationId: matchId,
            title: repairedTitle,
            messages: loadedMsgs,
            messagePagination: loadedPagination,
            attachedEndeavorIds: eid ? [eid] : (match.attachedEndeavorIds || [])
          };
          setInternalConversation(convObj);
          lastConvIdRef.current = matchId;

          // Populate the UI message list from loaded messages
          if (loadedMsgs.length > 0) {
            setMessages(loadedMsgs);
            setMessagePagination(loadedPagination);
            requestAnimationFrame(scrollChatToBottom);
          }

          onConversationActivated?.(convObj);

          // Cache for fast re-hydration on next remount
          if (eid) setCourseConvId(eid, matchId);

          // Repair title and/or persist course association to the backend
          const needsBackendPatch = needsTitleRepair ||
            !Array.isArray(match.attachedEndeavorIds) ||
            !match.attachedEndeavorIds.some(id => String(id) === eid);
          if (needsBackendPatch) {
            try {
              await patchConversation(matchId, {
                title: repairedTitle,
                attachedEndeavorIds: eid ? [eid] : []
              });
              setTimeout(() => { try { window.__chatHistoryUpsert?.(convObj); } catch {} }, 0);
            } catch (e) {
              console.warn('Endeavor hydrate: backend patch failed', e);
            }
          }
        }
      } catch (e) {
        console.warn('AIChatCard hydrate endeavor failed:', e);
      } finally {
        creatingEndeavorConvRef.current = false;
        setIsHydrating(false);
      }
    };
    hydrate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, endeavorId, endeavorName, user, conversation, internalConversation, loadExistingConversations, loadConversationMessages, loadConversationDetails, patchConversation, onConversationActivated]);

  // Hydrate existing document conversation only (no auto creation - backend will create on first message)
  useEffect(() => {
    const hydrate = async () => {
      if (mode !== 'document') return;
      // If parent provided conversation (conversation) OR internal already set, skip
      if (conversation || internalConversation) return;
      if (!documentName || !user) return;
      if (creatingDocumentConvRef.current) return;
      creatingDocumentConvRef.current = true;
      setIsHydrating(true);
      try {
        const existingList = await loadExistingConversations(user.id || user.userId);
        const desiredTitle = `Document: ${documentName}`;
        const match = existingList.find(c => c.title === desiredTitle || c.title === documentName);
        if (match) {
          let loadedMsgs = [];
          let loadedPagination = null;
          try {
            const loaded = await loadConversationMessages(match.id || match.conversationId, { page: 0, size: 10 });
            if (loaded && Array.isArray(loaded.messages)) loadedMsgs = loaded.messages;
            if (loaded && loaded.pagination) loadedPagination = loaded.pagination;
          } catch (e) {
            console.warn('Document hydrate: loadConversationMessages failed', e);
          }
          const convObj = {
            ...match,
            conversationId: match.id || match.conversationId,
            messages: loadedMsgs,
            messagePagination: loadedPagination
          };
          setInternalConversation(convObj);
          // Populate the UI message list from loaded messages
          if (loadedMsgs.length > 0) {
            setMessages(loadedMsgs);
            setMessagePagination(loadedPagination);
            requestAnimationFrame(scrollChatToBottom);
          }
          onConversationActivated?.(convObj);
        }
        // Removed auto-creation - backend will create conversation when first message is sent
      } catch (e) {
        console.warn('AIChatCard hydrate document failed:', e);
      } finally {
        creatingDocumentConvRef.current = false;
        setIsHydrating(false);
      }
    };
    hydrate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, documentName, user, conversation, internalConversation, loadExistingConversations, loadConversationMessages, onConversationActivated]);

  // Auto-select document context in document mode
  useEffect(() => {
    if (mode !== 'document') return;
    if (!documentId) return;
    setSelectedDocuments([String(documentId)]);
    setAutoDocsLoaded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, documentId]);

  // Auto-select & refresh endeavor documents whenever docsRefreshKey changes
  useEffect(() => {
    const loadEndeavorDocs = async () => {
      if (mode !== 'endeavor' || !endeavorId || !user) return;
      try {
        setLoadingDocs(true);
        console.log(`[AIChatCard] Loading endeavor docs (reason=effect) for endeavorId=${endeavorId}`);
        let docs = [];
        try {
          const resp = await DocumentEndeavorApi.getDocumentsByEndeavor(endeavorId);
          if (resp?.ok) {
            const raw = await resp.json();
            docs = Array.isArray(raw)
              ? raw
              : (Array.isArray(raw.documents) ? raw.documents : []);
            console.log('[AIChatCard] DocumentEndeavorApi returned', docs.length);
          } else {
            console.warn('[AIChatCard] DocumentEndeavorApi response not ok:', resp?.status);
          }
        } catch (e) {
          console.warn('[AIChatCard] Primary endeavor docs fetch failed:', e);
        }

        // Fallback if empty
        if (docs.length === 0) {
          try {
            const resp = await DocumentApi.getDocuments({ size: 500 });
            if (resp?.ok) {
              const data = await resp.json();
              const arr = Array.isArray(data) ? data : (data.content || data.documents || []);
              docs = arr.filter(d => (d.endeavorId || d.endeavor_id) === Number(endeavorId));
              console.log('[AIChatCard] Fallback doc fetch produced', docs.length);
            }
          } catch (e) {
              console.warn('[AIChatCard] Fallback generic documents fetch failed:', e);
          }
        }

        // Relaxed filtering: try processed subset else all
        let processed = docs.filter(d => {
          if (Array.isArray(d.statuses) && d.statuses.length > 0) {
            return d.statuses.some(s => (s.id || s.statusId) === 7);
          }
          return false;
        });
        if (processed.length === 0 && docs.length > 0) {
          console.log('[AIChatCard] No processed docs (status 7); falling back to all docs for context.');
          processed = docs;
        }

        const ids = processed
          .map(d => d.documentId || d.id)
          .filter(Boolean)
          .map(String);

        setSelectedDocuments(ids);
        console.log('[AIChatCard] Selected document IDs for context:', ids);
      } catch {
        setSelectedDocuments([]);
      } finally {
        setAutoDocsLoaded(true);
        setLoadingDocs(false);
      }
    };

    loadEndeavorDocs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, endeavorId, user, documentsRefreshKey]); // ✅ includes refresh key

  // Load more messages (older)
  const handleLoadMore = useCallback(async () => {
    if (!conversation?.conversationId || !messagePagination?.hasNext || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await loadMoreMessages(conversation.conversationId, messagePagination.page, messagePagination.size || 10);
      if (result.messages?.length) {
        setMessages(prev => [...result.messages, ...prev]); // prepend older
        setMessagePagination(result.pagination);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [conversation, messagePagination, loadMoreMessages, loadingMore]);

  // Cancel any in-flight stream on unmount
  useEffect(() => {
    return () => {
      try { activeStreamRef.current?.cancel?.(); } catch {}
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    };
  }, []);

  // Initialize Scout greeting when starting a new chat
  useEffect(() => {
    const conv = conversation || internalConversation;
    const convId = conv?.conversationId || conv?.id;
    const isPlaceholder = conv?.isPlaceholder;
    
    // Show greeting when:
    // 1. We have an active conversation, placeholder, OR no conversation yet (fresh state)
    // 2. No user messages have been sent yet  
    // 3. Not currently hydrating messages
    // 4. No greeting already set
    const shouldShowGreeting = (convId || isPlaceholder || conv || (!conv && !isHydrating)) && 
                              !hasUserMessages && 
                              !isHydrating && 
                              !scoutGreeting;
    
    if (shouldShowGreeting) {
      const randomGreeting = SCOUT_GREETINGS[Math.floor(Math.random() * SCOUT_GREETINGS.length)];
      setScoutGreeting(randomGreeting);
    }
    
    // Clear greeting when user messages exist
    if (hasUserMessages && scoutGreeting) {
      setScoutGreeting(null);
    }
  }, [conversation, internalConversation, hasUserMessages, isHydrating, scoutGreeting]);

  const submitMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // TEMP: premium gating — block send when user cannot create chat content
    if (!canCreate('chat')) {
      setShowChatUpgradeModal(true);
      return;
    }

    // Stop speech recognition if active
    if (isListening) stopListening();

    // Block sending in standard mode if no conversation has been created/selected
    // If autoCreateIfNone is set we allow first prompt to proceed and will lazily create the conversation.
    if (noActiveConversation) return;

    const userPlain = input.trim();

    // Track prompt sent event (queued)
    UsageEventsTracker.recordQueued({
      eventType: 'CHAT_PROMPT_SENT',
      payload: {
        action: 'submit',
        category: 'chat',
        entityType: 'conversation',
        entityId: (conversation?.conversationId || internalConversation?.conversationId) || null,
        value: userPlain.length,
        metadata: { mode, endeavorId, documentId, quality }
      }
    });

    const userTimestamp = new Date();
    const userMsg = {
      id: `local_${Date.now()}`,
      text: userPlain,
      sender: 'user',
      timestamp: userTimestamp
    };
    
    // Streaming AI placeholder message - ensure timestamp is after user message
    const aiId = `ai_${Date.now()}`;
    const streamingMsg = {
      id: aiId,
      text: '',
      sender: 'ai',
      timestamp: new Date(userTimestamp.getTime() + 1), // Ensure AI message is always after user message
      isThinking: true,
      isStreaming: true,
      reasoningText: '', // OpenAI reasoning/thought process - shown in thinking bubble
      documentReferences: [],
      suggestedActions: [],
      qualityLevel: quality
    };
    
    // Capture messages array with BOTH user and AI messages so reconciliation doesn't clear streaming
    const nextMessages = [...messages, userMsg, streamingMsg];
    setMessages(nextMessages);
  // Immediately scroll to bottom after user message queued
  requestAnimationFrame(scrollChatToBottom);
    setInput('');

    // Removed firstPromptSentRef tracking (simplified)

    setIsLoading(true);

    // Get current conversation reference (if any)
    // Backend will create conversation on first message if it doesn't exist
    let conv = conversation || internalConversation;
    const wasNewConversation = !conv || !(conv.conversationId || conv.id);

    // Notify parent (ScoutIQ dashboard) that a user message was sent in this conversation (if conversation exists)
    if (conv?.conversationId || conv?.id) {
      try { onUserMessageSent?.(conv.conversationId || conv.id, userMsg); } catch {}
    }
    // Scroll to bottom to reveal streaming placeholder
    requestAnimationFrame(scrollChatToBottom);

    // Build context to send: prefer endeavor_id (if course chat has one); else document_ids
    const endeavorIdForRequest =
      (mode === 'endeavor')
        ? (endeavorId || attachedEndeavorIds?.[0] || conversation?.attachedEndeavorIds?.[0] || null)
        : (conversation?.attachedEndeavorIds?.[0] || attachedEndeavorIds?.[0] || null);

    // Determine document context: prefer explicit user selection for this session.
    // If no selection, only fall back to conversation-attached docs if they exist.
    let docIdsForRequest = selectedDocuments?.length ? selectedDocuments : [];
    // Only fall back if user has not explicitly cleared docs
    if (!docIdsForRequest.length && !docsCleared) {
      const candidate = (conversation?.attachedDocumentIds || internalConversation?.attachedDocumentIds || []);
      if (Array.isArray(candidate) && candidate.length) {
        docIdsForRequest = candidate.map(String);
      }
    }

    // Now call the API (STREAM)
    try {
     // Cancel any prior in-flight stream
     try { activeStreamRef.current?.cancel?.(); } catch {}
     activeStreamRef.current = await processUserMessageStream(
       userPlain,
       {
         conversationId: conv?.conversationId || conv?.id || null, // null for new conversations - backend will create
         ...(endeavorIdForRequest
             ? { endeavorId: endeavorIdForRequest }
             : { selectedDocuments: docIdsForRequest }),
         quality_level: quality
       },
       {
         onReasoning: (reasoningPayload) => {
           if (!reasoningPayload) return;
           // Extract content from reasoning event: { type, id, summary, content }
           const content = typeof reasoningPayload === 'string' 
             ? reasoningPayload 
             : (reasoningPayload?.content || '');
           if (!content) return;
           
           setMessages(prev => prev.map(m => m.id === aiId
             ? ({
                 ...m,
                 reasoningText: (m.reasoningText || '') + content,
                 isThinking: true
               })
             : m
           ));
           requestAnimationFrame(scrollChatToBottom);
         },
         onMeta: (meta) => {
           console.log('[AiChatCard onMeta] Received metadata', { 
             hasConversationId: !!meta?.conversation_id, 
             hasTitle: !!meta?.conversation_title,
             title: meta?.conversation_title,
             wasNewConversation,
             currentTitle: conv?.title
           });
           
           // Helper to check if a title has Document: or Course: prefix (should not be auto-renamed)
           const hasProtectedPrefix = (title) => {
             if (!title) return false;
             const lower = title.trim().toLowerCase();
             return lower.startsWith('document:') || lower.startsWith('course:');
           };
           
           // Backend may return conversation_id in metadata for newly created conversations
           if (meta?.conversation_id && wasNewConversation) {
             // If we already have a conversation with this ID, only update title if provided AND not protected
             if (conv?.conversationId === meta.conversation_id) {
               // Already have this conversation, only update if we got a new title AND it's not protected
               if (meta.conversation_title && 
                   meta.conversation_title !== 'New Chat' && 
                   meta.conversation_title !== conv.title &&
                   !hasProtectedPrefix(conv.title)) {
                 const updatedConv = {
                   ...conv,
                   title: meta.conversation_title,
                   updatedAt: new Date().toISOString()
                 };
                 setInternalConversation(updatedConv);
                 onConversationActivated?.(updatedConv);
                 setTimeout(() => {
                   try { window.__chatHistoryUpsert?.(updatedConv); } catch {}
                 }, 0);
                 conv = updatedConv;
               }
               // Don't recreate the conversation if we already have it
               return;
             }
             
             // First time seeing this conversation_id, create it
             const courseTitle = isCourseChat ? (desiredCourseTitle || currentTitle) : (meta.conversation_title || meta.title || currentTitle);
             const newConv = {
               id: meta.conversation_id,
               conversationId: meta.conversation_id,
               title: courseTitle,
               messages: nextMessages,
               attachedDocumentIds: docIdsForRequest,
               attachedEndeavorIds: endeavorIdForRequest ? [endeavorIdForRequest] : [],
               updatedAt: new Date().toISOString()
             };
             setInternalConversation(newConv);
             lastConvIdRef.current = meta.conversation_id;
             
             // Cache courseId → conversationId for fast re-hydration
             if (isCourseChat && endeavorIdForRequest) {
               setCourseConvId(String(endeavorIdForRequest), meta.conversation_id);
             }
             
             // Persist course association to backend so it survives reload
             if (isCourseChat && endeavorIdForRequest) {
               setTimeout(async () => {
                 try {
                   await patchConversation(meta.conversation_id, {
                     title: courseTitle,
                     attachedEndeavorIds: [endeavorIdForRequest]
                   });
                 } catch (e) {
                   console.warn('Failed to persist course association:', e);
                 }
               }, 0);
             }
             
             // Always update parent and history for course chats; for others, only if we have a real title
             if (isCourseChat || (meta.conversation_title && meta.conversation_title !== 'New Chat')) {
               onConversationActivated?.(newConv);
               setTimeout(() => {
                 try { window.__chatHistoryUpsert?.(newConv); } catch {}
               }, 0);
             } else {
               onConversationActivated?.(newConv);
             }
             conv = newConv;
           } else if (meta?.conversation_title && meta.conversation_title !== 'New Chat' && conv?.conversationId) {
             // Update title if we already have a conversation and received a real title
             // BUT skip if current title has Document: or Course: prefix (protected)
             const hasProtectedPrefix = (title) => {
               if (!title) return false;
               const lower = title.trim().toLowerCase();
               return lower.startsWith('document:') || lower.startsWith('course:');
             };
             
             if (!hasProtectedPrefix(conv.title)) {
               const updatedConv = {
                 ...conv,
                 title: meta.conversation_title,
                 updatedAt: new Date().toISOString()
               };
               setInternalConversation(updatedConv);
               onConversationActivated?.(updatedConv);
               setTimeout(() => {
                 try { window.__chatHistoryUpsert?.(updatedConv); } catch {}
               }, 0);
               conv = updatedConv;
             }
           }
           
           setMessages(prev => prev.map(m => m.id === aiId ? ({
             ...m,
             documentReferences: meta?.chunks_metadata || meta?.sources || m.documentReferences,
             qualityLevel: meta?.quality_level || m.qualityLevel
           }) : m));
         },
         onToken: (chunk) => {
           if (!chunk) return;
           // Append to buffer instead of rendering immediately
           streamBufferRef.current += chunk;
           streamAiIdRef.current = aiId;

           // Start flush timer if not already running
           if (!flushTimerRef.current) {
             // First token: flush immediately to clear thinking state
             const doFlush = () => {
               const buf = streamBufferRef.current;
               if (!buf) return;
               // Find a word boundary to flush up to (prefer complete words)
               let end = buf.length;
               if (end > 12) {
                 const lastSpace = buf.lastIndexOf(' ', end);
                 if (lastSpace > 0) end = lastSpace + 1; // include the space
               }
               const chunk2 = buf.slice(0, end);
               streamBufferRef.current = buf.slice(end);
               setMessages(prev => prev.map(m => m.id === streamAiIdRef.current
                 ? ({ ...m, text: (m.text || '') + chunk2, reasoningText: '', isThinking: false, isStreaming: true })
                 : m
               ));
               // Throttled scroll: at most every 90ms
               const now = Date.now();
               if (now - lastScrollFlushRef.current > 90) {
                 lastScrollFlushRef.current = now;
                 requestAnimationFrame(scrollChatToBottom);
               }
             };
             doFlush(); // instant first flush
             flushTimerRef.current = setInterval(doFlush, 45);
           }
         },
         onDone: (payload) => {
           console.log('[AiChatCard onDone] Stream complete', { 
             hasConversationId: !!payload?.conversation_id, 
             wasNewConversation,
             currentConvId: conv?.conversationId 
           });
           
           // Backend may return conversation_id in final payload for newly created conversations
           if (payload?.conversation_id && wasNewConversation && !conv?.conversationId) {
             console.log('[AiChatCard onDone] Creating new conversation object', { 
               conversationId: payload.conversation_id,
               hasPayloadTitle: !!payload.conversation_title,
               payloadTitle: payload.conversation_title 
             });
             const doneCourseTitle = isCourseChat ? (desiredCourseTitle || currentTitle) : (payload.conversation_title || payload.title || currentTitle);
             const newConv = {
               id: payload.conversation_id,
               conversationId: payload.conversation_id,
               title: doneCourseTitle,
               messages: nextMessages,
               attachedDocumentIds: docIdsForRequest,
               attachedEndeavorIds: endeavorIdForRequest ? [endeavorIdForRequest] : [],
               updatedAt: new Date().toISOString()
             };
             setInternalConversation(newConv);
             onConversationActivated?.(newConv);
             // Cache courseId → conversationId for fast re-hydration
             if (isCourseChat && endeavorIdForRequest) {
               setCourseConvId(String(endeavorIdForRequest), payload.conversation_id);
             }
             // Persist course association to backend
             if (isCourseChat && endeavorIdForRequest) {
               setTimeout(async () => {
                 try {
                   await patchConversation(payload.conversation_id, {
                     title: doneCourseTitle,
                     attachedEndeavorIds: [endeavorIdForRequest]
                   });
                 } catch (e) {
                   console.warn('Failed to persist course association (onDone):', e);
                 }
               }, 0);
             }
             // Always add course chats to history panel; for others, only if real title
             if (isCourseChat || (payload.conversation_title && payload.conversation_title !== 'New Chat')) {
               setTimeout(() => {
                 try { window.__chatHistoryUpsert?.(newConv); } catch {}
               }, 0);
             }
             conv = newConv; // update reference
           } else if (payload?.conversation_title && payload.conversation_title !== 'New Chat' && conv?.conversationId) {
             // Update title if it changed and is meaningful
             // But never override a Course: or Document: prefixed title
             const existingTitle = conv.title || '';
             const existingLower = existingTitle.trim().toLowerCase();
             const isProtected = existingLower.startsWith('course:') || existingLower.startsWith('document:');
             if (!isProtected) {
             console.log('[AiChatCard onDone] Updating conversation title', { 
               from: conv.title, 
               to: payload.conversation_title 
             });
             const updatedConv = {
               ...conv,
               title: payload.conversation_title,
               updatedAt: new Date().toISOString()
             };
             setInternalConversation(updatedConv);
             // Update history panel (async to avoid setState during render)
             setTimeout(() => {
               try { window.__chatHistoryUpsert?.(updatedConv); } catch {}
             }, 0);
             conv = updatedConv;
             }
           }
           
           // Flush remaining buffer and stop timer
           clearInterval(flushTimerRef.current);
           flushTimerRef.current = null;
           const remaining = streamBufferRef.current;
           streamBufferRef.current = '';
           streamAiIdRef.current = null;

           console.log('[AiChatCard onDone] Finalizing AI message', { aiId, hasResponse: !!payload?.response });
           setMessages(prev => {
             const updated = prev.map(m => m.id === aiId ? ({
               ...m,
               isThinking: false,
               isStreaming: false,
               text: payload?.response ? payload.response : ((m.text || '') + remaining),
               tokenCount: payload?.tokens_used ?? m.tokenCount
             }) : m);
             // ── Math debug: log the final accumulated AI text ──
             const finalMsg = updated.find(m => m.id === aiId);
             if (finalMsg?.text) {
               console.warn('[AiChatCard MATH_DEBUG] final AI text', {
                 length: finalMsg.text.length,
                 first600: finalMsg.text.slice(0, 600),
                 last300: finalMsg.text.slice(-300),
                 hasDD: finalMsg.text.includes('$$'),
                 hasBackslash: finalMsg.text.includes('\\'),
               });
             }
             console.log('[AiChatCard onDone] Messages after finalization', { count: updated.length, aiMsg: updated.find(m => m.id === aiId) });
             return updated;
           });
           requestAnimationFrame(scrollChatToBottom);
           
           // ✅ FIX: Update the conversation object with messages after render completes
           // Store current messages in a ref to avoid stale closure
           if (conv?.conversationId) {
             setTimeout(() => {
               // Get current messages from state
               setMessages(currentMessages => {
                 // Update conversation with current messages
                 const updatedConvWithMessages = {
                   ...conv,
                   messages: currentMessages,
                   messagePagination: messagePagination,
                   updatedAt: new Date().toISOString()
                 };
                 setInternalConversation(updatedConvWithMessages);
                 console.log('[AiChatCard onDone] Updated conversation object with messages', { conversationId: conv.conversationId, messageCount: currentMessages.length });
                 
                 // Schedule history update in another tick to avoid React warning
                 setTimeout(() => {
                   try { window.__chatHistoryUpsert?.(updatedConvWithMessages); } catch {}
                 }, 0);
                 
                 return currentMessages; // Don't modify messages, just read them
               });
             }, 0);
           }
           
           // Track AI response received event
           UsageEventsTracker.recordQueued({
             eventType: 'CHAT_RESPONSE_RECEIVED',
             payload: {
               action: 'receive',
               category: 'chat',
               entityType: 'conversation',
               entityId: conv?.conversationId || conv?.id || null,
               metadata: { tokensUsed: payload?.tokens_used, quality }
             }
           });
           
           console.log('[AiChatCard onDone] Clearing stream state');
           activeStreamRef.current = null;
           setIsLoading(false);
           
           // ✅ FIX: Refresh title from backend if we didn't receive a meaningful title in the stream
           // Check if conversation still has default title ('New Chat') and we didn't get a real title in payload
           const hasRealTitle = payload?.conversation_title && payload.conversation_title !== 'New Chat';
           const convHasRealTitle = conv?.title && conv.title !== 'New Chat';
           if (wasNewConversation && (conv?.conversationId || conv?.id) && !hasRealTitle && !convHasRealTitle) {
             setTimeout(() => {
               try { window.__chatHistoryRefreshTitle?.(conv.conversationId || conv.id); } catch {}
             }, 800);
           }
         },
         onError: (err) => {
           clearInterval(flushTimerRef.current);
           flushTimerRef.current = null;
           streamBufferRef.current = '';
           streamAiIdRef.current = null;
           setMessages(prev => prev.map(m => m.id === aiId ? ({
             ...m,
             isThinking: false,
             isStreaming: false,
             text: m.text || (typeof err === 'string' ? err : 'There was a problem generating a response.')
           }) : m));
           activeStreamRef.current = null;
           setIsLoading(false);
         }
       }
     );
    } catch {
     setMessages(prev => prev.map(m => m.id === aiId ? ({
       ...m,
        id: `err_${Date.now()}`,
       text: 'Error contacting AI service. Please retry.',
       isThinking: false,
       isStreaming: false
     }) : m));
     setIsLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // 2) RENAME MODAL LOGIC
  // ------------------------------------------------------------------
  const openRenameModal = () => {
    if (isCourseChat || isDocumentChat) return;
    setRenameInput(currentTitle);
    setShowRenameModal(true);
  };

  const invalidRename =
    !renameInput.trim() ||
    /^course:\s*/i.test(renameInput.trim()) ||
    /^document:\s*/i.test(renameInput.trim());

  const handleRenameSave = useCallback(async () => {
    if (invalidRename || renameSavingRef.current) return;
    renameSavingRef.current = true;
    try {
      const newTitle = renameInput.trim();
      if (newTitle === currentTitle) {
        setShowRenameModal(false);
        return;
      }
      // Use current conversation ref (prop or internal)
      const convRef = conversation || internalConversation;
      if (convRef?.conversationId) {
        const ok = await onUpdateConversationTitle?.(convRef.conversationId, newTitle);
        if (ok) {
          convRef.title = newTitle;
          if (convRef === internalConversation) {
              setInternalConversation({ ...convRef });
          }
          onConversationActivated?.({ ...convRef });
        }
      }
      setShowRenameModal(false);
    } finally {
      renameSavingRef.current = false;
    }
  }, [
  invalidRename,
  renameInput,
  currentTitle,
  conversation,
  internalConversation,
  onUpdateConversationTitle,
  onConversationActivated
]);

  // Close on ESC
  useEffect(() => {
    if (!showRenameModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowRenameModal(false);
      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleRenameSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showRenameModal, handleRenameSave]);

  // ===== Missing helper functions =====

  // Open documents modal (blocked for course/endeavor chats and document mode, except in Scout's Desk)
  const openDocModal = useCallback(() => {
    if (variant !== 'scoutIqDashboard' && (isCourseChat || isDocumentChat || mode === 'endeavor' || mode === 'document')) return;
    setIsDocModalOpen(true);
  }, [variant, isCourseChat, isDocumentChat, mode, setIsDocModalOpen]);

  // Toggle document selection (disabled when locked, except in Scout's Desk)
  const handleToggleDoc = useCallback((docId) => {
    if (variant !== 'scoutIqDashboard' && (isCourseChat || isDocumentChat || mode === 'endeavor' || mode === 'document')) return;
    setSelectedDocuments(prev => {
      const next = prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId];
      // Reset docsCleared if any docs are selected
      if (next.length > 0 && docsCleared) setDocsCleared(false);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, isCourseChat, isDocumentChat, mode, docsCleared]);

  // Quick-upload: handle file selection from the "+" button
  const handleQuickUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input so re-selecting the same file triggers onChange
    e.target.value = '';

    setIsUploading(true);
    try {
      // Resolve "Article" document type ID (required by backend)
      const docTypes = dataContext?.documentTypes || [];
      const articleType = docTypes.find(dt => dt.name === 'Article');
      const documentTypeId = articleType?.id;

      const metadata = {
        name: file.name,
        documentTypeId: documentTypeId || undefined,
        userId: user?.user_id || user?.userId || user?.id,
        description: `Uploaded file: ${file.name}`,
      };
      const response = await FileApi.uploadFile(file, metadata);
      if (!response.ok) {
        console.error('Quick upload failed:', response.status);
        return;
      }
      const data = await response.json();
      const documentId = data.documentId || data.document?.documentId || data.document?.document_id || data.id;
      if (documentId) {
        const docIdStr = String(documentId);
        // Add to selected documents
        setSelectedDocuments(prev => prev.includes(docIdStr) ? prev : [...prev, docIdStr]);
        if (docsCleared) setDocsCleared(false);
        // Store the file name immediately for the chip
        setDocNames(prev => ({ ...prev, [docIdStr]: file.name }));
        // Broadcast so document lists elsewhere update
        try {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('documents:changed', { detail: { source: 'QuickUpload', fileName: file.name, documentId } }));
          }, 140);
        } catch (_) {}
      }
    } catch (err) {
      console.error('Quick upload error:', err);
    } finally {
      setIsUploading(false);
    }
  }, [user, dataContext, setSelectedDocuments, docsCleared]);

  // Resolve document names for selected documents
  useEffect(() => {
    if (!selectedDocuments.length) return;
    const state = dataContext?.state;
    const missing = selectedDocuments.filter(id => !docNames[id]);
    if (!missing.length) return;

    // Try synchronous resolution from DataContext state first
    const resolved = {};
    const stillMissing = [];
    for (const id of missing) {
      const doc = state?.documentsById?.[id] || state?.documentsById?.[Number(id)];
      if (doc?.name || doc?.title) {
        resolved[id] = doc.name || doc.title;
      } else {
        stillMissing.push(id);
      }
    }
    if (Object.keys(resolved).length) {
      setDocNames(prev => ({ ...prev, ...resolved }));
    }

    // Async fetch for any still missing
    if (stillMissing.length && dataContext?.getDocumentById) {
      (async () => {
        const fetched = {};
        for (const id of stillMissing) {
          try {
            const doc = await dataContext.getDocumentById(Number(id));
            if (doc?.name || doc?.title) fetched[id] = doc.name || doc.title;
          } catch (_) {}
        }
        if (Object.keys(fetched).length) {
          setDocNames(prev => ({ ...prev, ...fetched }));
        }
      })();
    }
  }, [selectedDocuments, dataContext, docNames]);

  // Remove a document from selection (used by attached-docs strip chips)
  const handleRemoveDoc = useCallback((docId) => {
    setSelectedDocuments(prev => {
      const next = prev.filter(id => id !== docId);
      if (next.length === 0) setDocsCleared(true);
      return next;
    });
  }, [setSelectedDocuments]);

  // Add this effect so selectedDocuments and attachedEndeavorIds are set when a chat is picked
  // Also sync from internalConversation so that document selection persists after sending messages
  useEffect(() => {
    const conv = conversation || internalConversation;
    if (!conv) return;
    if (Array.isArray(conv.attachedDocumentIds) && conv.attachedDocumentIds.length > 0) {
      setSelectedDocuments(conv.attachedDocumentIds.map(String));
    }
    if (Array.isArray(conv.attachedEndeavorIds) && conv.attachedEndeavorIds.length > 0) {
      setAttachedEndeavorIds(conv.attachedEndeavorIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, internalConversation]);

  // === ADD THESE HELPERS (were missing) ===
  // Removed unused formatDocNames helper (was not referenced)

  const toggleRefsExpand = useCallback((msgId) => {
    setExpandedRefs(prev => {
      const next = new Set(prev);
      const isExpanding = !next.has(msgId);
      if (isExpanding) next.add(msgId); else next.delete(msgId);
      if (isExpanding) {
        // Scroll down after the chips render so they're visible
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollChatToBottom);
        });
      }
      return next;
    });
  }, [scrollChatToBottom]);

  const getUniqueDocNames = useCallback((refs) => {
    if (!Array.isArray(refs)) return [];
    return [...new Set(refs.map(r => r.documentName || r.name).filter(Boolean))];
  }, []);
  // === END ADDED HELPERS ===

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  // Thinking status label (context-aware, rotating)
  // Only show doc-grounded labels when documents are explicitly selected/attached for this prompt
  const hasDocContext = selectedDocuments.length > 0;
  const isThinkingActive = messages.some(m => m.isThinking && !m.text);
  const thinkingLabel = useThinkingLabel(isThinkingActive, hasDocContext);

  const isEmpty = messages.length === 0 && !isHydrating; // no messages and not currently hydrating

  return (
    <div className={`ai-chat-card mode-${mode} ${loadingDocs ? 'loading-docs' : ''} ${isEmpty ? 'empty' : ''} ${variant === 'scoutIqDashboard' ? 'variant-scoutiq' : ''}${isDragging ? ' chat-dragover' : ''}`} {...dropzoneHandlers}>
      {/* Full-card drop overlay */}
      {isDragging && (
        <div className="chat-drop-overlay" aria-hidden="true">
          <div className="chat-drop-overlay__inner">
            <span className="chat-drop-overlay__icon" aria-hidden="true">📎</span>
            <span className="chat-drop-overlay__text">Drop files to attach them to this chat</span>
          </div>
        </div>
      )}
      {variant !== 'scoutIqDashboard' && !hideHeader && (
      <div className="ai-chat-header">
        {/* Row 1 — Context info */}
        <div className="header-context-row">
          <div className="header-context-info">
            <button
              type="button"
              className={`chat-title-btn ${(isCourseChat || isDocumentChat) ? 'locked' : ''}`}
              onClick={openRenameModal}
              disabled={isCourseChat || isDocumentChat}
              title={(isCourseChat || isDocumentChat) ? 'Chat title is fixed' : 'Click to rename chat'}
            >
              {isCourseChat && <span className="title-icon" aria-hidden="true">📚</span>}
              {isDocumentChat && <span className="title-icon" aria-hidden="true">📄</span>}
              {!isCourseChat && !isDocumentChat && <span className="title-icon" aria-hidden="true">💬</span>}
              <span className="title-text">{displayTitle}</span>
            </button>
            {onToggleExpansion && (
              <button
                type="button"
                className={`expand-btn ui-btn ui-btn--neutral ${isExpanded ? 'expanded' : ''}`}
                onClick={onToggleExpansion}
                title={isExpanded ? 'Collapse chat to half view' : 'Expand chat to 50/50 view'}
              >
                {isExpanded ? '⤢' : '⤡'}
              </button>
            )}
          </div>
          <p className="header-context-desc">
            {isCourseChat
              ? 'Using all processed course documents as context'
              : isDocumentChat
                ? 'Using this document as context'
                : selectedDocuments.length > 0
                  ? `Using ${selectedDocuments.length} selected document${selectedDocuments.length !== 1 ? 's' : ''} as context`
                  : 'No documents selected — select docs to ground responses'}
            {!isAiServiceAvailable && (
              <span className="status-badge spinning" role="status" aria-live="polite">
                <span className="ai-spinner" aria-hidden="true"></span>
                Spinning up AI
              </span>
            )}
          </p>
        </div>

        {/* Row 2 — Controls */}
        <div className="header-controls-row">
          {/* Left: Context selector */}
          {mode === 'standard' && (variant === 'scoutIqDashboard' || (!isCourseChat && !isDocumentChat)) ? (
            <button
              type="button"
              className="ctx-selector-btn"
              onClick={openDocModal}
              title="Select documents for chat context"
            >
              <span className="ctx-icon" aria-hidden="true">📄</span>
              <span className="ctx-label">
                Context: {selectedDocuments.length === 0
                  ? 'No Documents'
                  : `${selectedDocuments.length} Document${selectedDocuments.length !== 1 ? 's' : ''}`}
              </span>
              <span className="ctx-chevron" aria-hidden="true">›</span>
            </button>
          ) : (
            <div className="ctx-selector-btn ctx-locked">
              <span className="ctx-icon" aria-hidden="true">{isCourseChat ? '📚' : '📄'}</span>
              <span className="ctx-label">
                Context: {isCourseChat ? 'Course Documents' : 'Document'}
              </span>
            </div>
          )}

          {/* Right: Response mode */}
          <div className="quality-selector">
            <button
              type="button"
              className={`mode-trigger-btn ${showQuality ? 'open' : ''}`}
              onClick={() => setShowQuality(o => !o)}
              aria-haspopup="menu"
              aria-expanded={showQuality}
            >
              <span className="mode-trigger-label">
                {getQualityLevelOptions().find(o => o.value === quality)?.label || '⚖️ Balanced'}
              </span>
              <span className="mode-trigger-chevron" aria-hidden="true">{showQuality ? '▲' : '▼'}</span>
            </button>
            {showQuality && (
              <div className="quality-menu" role="menu">
                {getQualityLevelOptions().map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={opt.value === quality}
                    className={`quality-option ${opt.value === quality ? 'active' : ''}`}
                    onClick={() => { setQuality(opt.value); setShowQuality(false); }}
                  >
                    <div className="q-label">{opt.label}</div>
                    <div className="q-desc">{opt.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <div className="ai-chat-body" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
        {isHydrating && messages.length === 0 && (
          <div className="chat-hydrating" role="status" aria-live="polite">
            <div className="chat-hydrating-spinner" aria-hidden="true"></div>
            <span className="chat-hydrating-text">Loading conversation…</span>
          </div>
        )}
        {messagePagination?.hasNext && (
          <button
            className="load-older-btn ui-btn ui-btn--neutral"
            disabled={loadingMore}
            onClick={handleLoadMore}
          >
            {loadingMore ? 'Loading…' : 'Load older messages'}
          </button>
        )}

        {/* Scout welcome message for new chats - now in body flow */}
        {scoutGreeting && !hasUserMessages && (
          <div className="course-chat-hero">
            <div className="scout-welcome-image">
              <img 
                src="/scout_images/Scout_Thumbsup.png" 
                alt="Scout greeting" 
                className="scout-wave-image"
              />
            </div>
            <div className="course-chat-greeting">
              {scoutGreeting}
            </div>
          </div>
        )}

        <div className="messages">
          {messages
            .filter(m => m.text !== '[Content truncated to save space]')
            .map(m => {
            const hasDocRefs = Array.isArray(m.documentReferences) && m.documentReferences.length > 0;
            return (
              <div key={m.id} className={`msg-row ${m.sender}`}>
                {m.sender === 'ai' && (
                  <img src={botHead} alt="Scout" className="msg-avatar" />
                )}
                {(m.isThinking && !m.text) ? (
                  <div className="assistant-loading-row">
                    <div className="assistant-status">
                      {m.reasoningText ? (
                        <span className="assistant-status-label">{m.reasoningText}</span>
                      ) : (
                        <span className="assistant-status-label">{thinkingLabel}<span className="assistant-status-ellipsis"></span></span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bubble">
                    <div className="text">
                      <RichMarkdown text={m.text} isStreaming={!!m.isStreaming} />
                      {m.isStreaming && <span className="streaming-caret" aria-hidden="true" />}
                    </div>
                    {m.sender === 'ai' && m.text && !m.isStreaming && (
                      <button
                        type="button"
                        className={`assistant-copy-btn${copiedMsgId === m.id ? ' copied' : ''}`}
                        title="Copy response"
                        aria-label={copiedMsgId === m.id ? 'Response copied to clipboard' : 'Copy response'}
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(m.text).then(() => {
                              setCopiedMsgId(m.id);
                              setTimeout(() => setCopiedMsgId(id => id === m.id ? null : id), 1250);
                            });
                          } catch (err) {
                            console.warn('Copy failed', err);
                          }
                        }}
                      >
                        {copiedMsgId === m.id ? (
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                            <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" strokeWidth="1.2"/>
                          </svg>
                        )}
                      </button>
                    )}
                    {hasDocRefs && m.sender === 'ai' && !m.isStreaming && (() => {
                      const docNamesList = getUniqueDocNames(m.documentReferences);
                      const expanded = expandedRefs.has(m.id);
                      const VISIBLE_LIMIT = 3;
                      const visible = expanded ? docNamesList : docNamesList.slice(0, VISIBLE_LIMIT);
                      const hasMore = docNamesList.length > VISIBLE_LIMIT && !expanded;
                      return (
                        <div className="provenance-footer">
                          <button
                            type="button"
                            className="provenance-pill"
                            onClick={() => toggleRefsExpand(m.id)}
                            aria-expanded={expanded}
                            aria-label={`${docNamesList.length} document${docNamesList.length !== 1 ? 's' : ''} used — click to ${expanded ? 'collapse' : 'expand'}`}
                          >
                            <svg className="provenance-pill__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                              <path d="M4 1h5.586a1 1 0 0 1 .707.293l2.414 2.414A1 1 0 0 1 13 4.414V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                              <path d="M5 8h6M5 10.5h4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                            </svg>
                            <span className="provenance-pill__label">{docNamesList.length} document{docNamesList.length !== 1 ? 's' : ''} used</span>
                            <svg className={`provenance-pill__chevron${expanded ? ' expanded' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {expanded && (
                            <div className="provenance-chips" role="list">
                              {visible.map(name => (
                                <span key={name} role="listitem" className="provenance-chip">{name}</span>
                              ))}
                              {hasMore && (
                                <button
                                  type="button"
                                  className="provenance-chip provenance-chip--more"
                                  onClick={() => toggleRefsExpand(m.id)}
                                >
                                  +{docNamesList.length - VISIBLE_LIMIT} more
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {m.suggestedActions?.length > 0 && (
                      <div className="suggested">
                        {m.suggestedActions.slice(0, 4).map((a, i) => (
                          <button
                            key={i}
                            type="button"
                            className="ui-btn ui-btn--neutral suggested-action"
                            onClick={() => setInput(a.replace(/_/g, ' '))}
                          >
                            {a.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="stamp">
                      {m.sender === 'user' && new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </div>

      {/* ─── Floating Input Wrapper ─── */}
      <div className="chat-input-wrapper-float">
        {/* Scroll-to-bottom arrow */}
        {showScrollBtn && (
          <button
            type="button"
            className="scroll-to-bottom-btn"
            onClick={handleScrollToBottomClick}
            aria-label="Scroll to bottom"
            title="Scroll to newest messages"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Attached documents strip + pending uploads */}
        {((!hideHeader && selectedDocuments.length > 0) || pendingUploads.length > 0) && (
          <div className="attached-docs-strip">
            {!hideHeader && selectedDocuments.map(id => (
              <span key={id} className="doc-chip" title={docNames[id] || `Document ${id}`}>
                <span className="doc-chip-icon" aria-hidden="true">📄</span>
                <span className="doc-chip-name">{docNames[id] || `Doc ${id}`}</span>
                <button
                  type="button"
                  className="doc-chip-remove"
                  onClick={() => handleRemoveDoc(id)}
                  aria-label={`Remove attachment ${docNames[id] || `Document ${id}`}`}
                >
                  ✕
                </button>
              </span>
            ))}
            {pendingUploads.map(pu => (
              <span key={pu.localId} className={`doc-chip${pu.status === 'uploading' ? ' doc-chip--uploading' : ''}${pu.status === 'error' ? ' doc-chip--error' : ''}`} title={pu.error || pu.name}>
                {pu.isImage && pu.thumbUrl ? (
                  <img src={pu.thumbUrl} alt="" className="doc-chip-thumb" />
                ) : (
                  <span className="doc-chip-icon" aria-hidden="true">{pu.status === 'error' ? '⚠️' : '📄'}</span>
                )}
                <span className="doc-chip-name">{pu.status === 'error' ? pu.error : pu.name}</span>
                {pu.status === 'uploading' && <span className="doc-chip-spinner" />}
                <button
                  type="button"
                  className="doc-chip-remove"
                  onClick={() => removePendingUpload(pu.localId)}
                  aria-label={`Remove attachment ${pu.name}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        <form className={`chat-input-bar${isInputExpanded ? ' chat-input-bar--expanded' : ''}`} onSubmit={submitMessage}>
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={noActiveConversation ? "Select or create a chat from the left panel…" : "Ask a question..."}
            value={input}
            onChange={e => {
              const val = e.target.value;
              setInput(val);
              const el = e.target;
              el.style.height = 'auto';
              const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 22;
              const scrollH = el.scrollHeight;
              setIsInputExpanded(prev => {
                if (prev) {
                  // Already expanded — only collapse when text is clearly single-line
                  return val.includes('\n') || val.length > 40;
                }
                // Not expanded — expand when content wraps to 2+ lines
                return scrollH > lineHeight * 1.5;
              });
              const maxH = window.innerHeight * 0.5;
              const newHeight = Math.min(scrollH, maxH);
              el.style.height = newHeight + 'px';
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitMessage(e);
              }
            }}
            disabled={isLoading || isHydrating || !isAiServiceAvailable || noActiveConversation}
            className="chat-input"
          />
          <div className="chat-input-actions">
            <div className="chat-input-actions__left">
              {mode !== 'endeavor' && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.pptx,.txt,.md,.csv,.json,.html,.rtf,.jpg,.jpeg,.png,.tiff,.tif,.bmp,.gif,.webp"
                    onChange={handleQuickUpload}
                    style={{ display: 'none' }}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="attach-btn"
                    onClick={() => setShowAttachModal(true)}
                    disabled={isUploading}
                    title="Add a document"
                    aria-label="Add a document"
                  >
                    {isUploading ? (
                      <span className="attach-spinner" />
                    ) : (
                      '+'
                    )}
                  </button>
                </>
              )}
            </div>
            <div className="chat-input-actions__right">
              {isSpeechSupported && (
                <button
                  type="button"
                  className={`mic-btn${isListening ? ' mic-btn--listening' : ''}`}
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading || isHydrating || !isAiServiceAvailable || noActiveConversation}
                  aria-label={isListening ? 'Stop speech input' : 'Start speech input'}
                  title={isListening ? 'Stop listening' : 'Voice input'}
                >
                  <svg className="mic-btn__icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="1" width="6" height="12" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                  </svg>
                </button>
              )}
              <button
                type="submit"
                className="send-btn"
                disabled={
                  !input.trim() ||
                  isLoading ||
                  isHydrating ||
                  !isAiServiceAvailable ||
                  noActiveConversation ||
                  hasAnyUploading ||
                  (mode === 'endeavor' && !autoDocsLoaded && loadingDocs)
                }
                aria-label="Send message"
              >
                {isLoading ? (
                  <span className="send-btn__spinner" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M8 13V3M4 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="chat-rename-modal-backdrop" onMouseDown={() => setShowRenameModal(false)}>
          <div
            className="chat-rename-modal"
            onMouseDown={e => e.stopPropagation()}
          >
            <h3>Rename Chat</h3>
            <input
              autoFocus
              value={renameInput}
              onChange={e => setRenameInput(e.target.value)}
              maxLength={80}
              placeholder="Enter a new chat title"
              className={invalidRename ? 'invalid' : ''}
            />
            {invalidRename && (
              <div className="rename-hint">
                Title cannot be empty or start with "Course:"
              </div>
            )}
            <div className="rename-actions">
              <button
                type="button"
                className="btn-cancel ui-btn ui-btn--neutral"
                onClick={() => setShowRenameModal(false)}
              >Cancel</button>
              <button
                type="button"
                className="btn-save ui-btn ui-btn--primary"
                disabled={invalidRename}
                onClick={handleRenameSave}
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {isDocModalOpen && mode === 'standard' && (variant === 'scoutIqDashboard' || !isCourseChat) && (
        <DocumentSelectionModal
          isOpen={isDocModalOpen}
          selectedDocuments={selectedDocuments}
          onToggleDocument={handleToggleDoc}
          onConnectDocuments={(arr) => {
            setSelectedDocuments(arr);
            setDocsCleared(arr.length === 0);
            setIsDocModalOpen(false);
          }}
          onClose={() => setIsDocModalOpen(false)}
          requiredStatusIds={requiredStatusIds}
          title="Select Documents for Context"
          description="Choose processed documents to ground the AI responses."
        />
      )}

      {/* Choice modal for "+" attach button */}
      <AttachDocumentModal
        isOpen={showAttachModal}
        onClose={() => setShowAttachModal(false)}
        onUploadNew={() => fileInputRef.current?.click()}
        onSelectExisting={() => setIsDocModalOpen(true)}
      />

      {/* TEMP: Upgrade modal for premium gating preview */}
      <UpgradeModal
        isOpen={showChatUpgradeModal}
        onClose={() => setShowChatUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier="basic"
        featureName="AI Chat"
        featureDescription="AI Chat requires a Basic plan. Upgrade now to send messages and get AI-powered answers."
        triggerSource="chat"
      />
    </div>
  );
}